import { RichTextNode } from '@enview/interface/types/actions/UserAction';
import base, { ApiTag } from './base';
import CustomField, {
  BillCustomField,
  CustomFieldType,
} from '@enview/interface/types/CustomFields';

interface CustomFieldParams {
  orgId: number;
  title: string;
  description?: string;
  type?: CustomFieldType;
}

export type BillFieldValueResponse = {
  billFieldValue: Partial<BillCustomField>;
  teamsAffected: number;
};

export interface BillFieldValue {
  teams: number[];
  value: { richText: RichTextNode };
  orgId: number;
  shouldArchive?: boolean;
  valueId?: number;
}

const SEARCH_BILL_LIST = 'SearchBillList';

const customFieldsAPI = base.injectEndpoints({
  endpoints: (build) => ({
    getOrganizationCustomFields: build.query<CustomField[], number>({
      query: (orgId) => ({
        url: `/v2/custom-fields/${orgId}`,
        method: 'GET',
      }),
      providesTags: [{ type: ApiTag.ORGANIZATION_CUSTOM_FIELD }],
    }),
    createCustomField: build.mutation<CustomField, CustomFieldParams>({
      query: (data) => {
        const { orgId, title, description, type } = data;
        return {
          url: `/v2/custom-fields/${orgId}`,
          method: 'POST',
          data: {
            title,
            description,
            type,
          },
        };
      },
      invalidatesTags: [ApiTag.ORGANIZATION_CUSTOM_FIELD],
    }),
    updateCustomField: build.mutation<
      CustomField,
      Partial<CustomFieldParams> & { id: number }
    >({
      query: (data) => {
        const { orgId, title, description, type, id } = data;
        return {
          url: `/v2/custom-fields/${orgId}/${id}`,
          method: 'PATCH',
          data: {
            title,
            description,
            type,
          },
        };
      },
      invalidatesTags: [ApiTag.ORGANIZATION_CUSTOM_FIELD],
    }),
    deleteCustomField: build.mutation<boolean, { id: number; orgId: number }>({
      query: ({ id, orgId }) => ({
        url: `/v2/custom-fields/${orgId}/${id}/archive`,
        method: 'POST',
      }),
      invalidatesTags: [ApiTag.ORGANIZATION_CUSTOM_FIELD],
    }),
    upsertCustomFieldValue: build.mutation<
      BillFieldValueResponse,
      { fieldId: number; billId: string; data: BillFieldValue }
    >({
      query: ({ fieldId, billId, data }) => ({
        url: `/v2/custom-fields/values/${fieldId}/bills/${billId}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: ApiTag.BILL_TAG, id: result.billFieldValue.billId },
              { type: ApiTag.BILL_TAG, id: SEARCH_BILL_LIST },
            ]
          : [],
    }),
  }),
  overrideExisting: false,
});
export default customFieldsAPI;
